import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

const STORAGE_KEYS = {
  shop: 'token_shop',
  storeName: 'token_store_name',
  clientId: 'token_client_id',
  clientSecret: 'token_client_secret',
};

// Production https://urchin-app-b69tp.ondigitalocean.app/token: cùng origin nếu dùng server-production.js; hoặc set REACT_APP_TOKEN_API_URL nếu proxy ở domain khác.
const TOKEN_API_BASE = process.env.REACT_APP_TOKEN_API_URL || '';
const ADD_SHOPIFY_URL = `${process.env.REACT_APP_DOMAIN || 'https://seahorse-app-akxsf.ondigitalocean.app'}/addshopify`;

function Token() {
  const [searchParams] = useSearchParams();
  const [shop, setShop] = useState('');
  const [storeName, setStoreName] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [code, setCode] = useState('');
  const [accessTokenResult, setAccessTokenResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addShopifyLoading, setAddShopifyLoading] = useState(false);
  const [addShopifyMessage, setAddShopifyMessage] = useState(null); // { type: 'success' | 'error', text: string }
  const [addShopifyDone, setAddShopifyDone] = useState(false);

  // Bước 1: Đọc params khi vào trang (hoặc khi redirect về)
  useEffect(() => {
    const shopParam = searchParams.get('shop');
    const codeParam = searchParams.get('code');

    if (shopParam) {
      const normalized = shopParam.startsWith('http') ? new URL(shopParam).hostname : shopParam;
      setShop(normalized);
      sessionStorage.setItem(STORAGE_KEYS.shop, normalized);
    } else {
      const saved = sessionStorage.getItem(STORAGE_KEYS.shop);
      if (saved) setShop(saved);
    }

    if (codeParam) {
      setCode(codeParam);
    }

    const savedStoreName = sessionStorage.getItem(STORAGE_KEYS.storeName);
    const savedClientId = sessionStorage.getItem(STORAGE_KEYS.clientId);
    const savedClientSecret = sessionStorage.getItem(STORAGE_KEYS.clientSecret);
    if (savedStoreName) setStoreName(savedStoreName);
    if (savedClientId) setClientId(savedClientId);
    if (savedClientSecret) setClientSecret(savedClientSecret);
  }, [searchParams]);

  // Khi đã có code (vừa redirect về) → tự gọi exchange token nếu đã có client_id + client_secret
  useEffect(() => {
    if (!code || !shop || !clientId || !clientSecret || accessTokenResult) return;

    const exchangeToken = async () => {
      setLoading(true);
      setError('');
      try {
        // Gọi qua proxy (server.js hoặc backend REACT_APP_DOMAIN) để tránh CORS
        const res = await fetch(`${TOKEN_API_BASE}/api/shopify/access_token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shop,
            client_id: clientId,
            client_secret: clientSecret,
            code,
          }),
        });

        const { ok, status, data, error: proxyError } = await res.json().catch(() => ({}));
        if (!res.ok || !ok) {
          setAccessTokenResult({ error: true, status: status || res.status, body: data });
          setError(data?.error_description || data?.error || proxyError || res.statusText);
        } else {
          setAccessTokenResult({ error: false, data: data || {} });
        }
      } catch (err) {
        setError(err.message || 'Network error. Chạy proxy: npm run server');
        setAccessTokenResult({ error: true, message: err.message });
      } finally {
        setLoading(false);
      }
    };

    exchangeToken();
  }, [code, shop, clientId, clientSecret]); // Chỉ chạy khi có đủ code + shop + client và chưa có kết quả

  // Khi đã có access token → gọi addshopify (name + token)
  useEffect(() => {
    const token = accessTokenResult?.data?.access_token;
    if (!token || addShopifyDone || addShopifyLoading) return;
    const name = storeName?.trim() || shop || '';

    const callAddShopify = async () => {
      setAddShopifyLoading(true);
      setAddShopifyMessage(null);
      try {
        const res = await fetch(ADD_SHOPIFY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, token }),
        });
        const data = await res.json().catch(() => ({}));
        const status = data?.status;
        if (status === 0) {
          setAddShopifyMessage({ type: 'success', text: 'Đã thêm store vào hệ thống fulfilment thành công.' });
        } else {
          setAddShopifyMessage({
            type: 'error',
            text: (data?.message || data?.msg || 'Có lỗi xảy ra.') + ' Vui lòng nhắn tin báo IT.',
          });
        }
      } catch (err) {
        setAddShopifyMessage({
          type: 'error',
          text: (err.message || 'Lỗi kết nối.') + ' Vui lòng nhắn tin báo IT.',
        });
      } finally {
        setAddShopifyLoading(false);
        setAddShopifyDone(true);
      }
    };

    callAddShopify();
  }, [accessTokenResult?.data?.access_token, addShopifyDone, addShopifyLoading, storeName, shop]);

  const handleAuthorize = () => {
    if (!shop.trim()) {
      setError('Chưa có shop. Truy cập với params có &shop=...');
      return;
    }
    if (!clientId.trim() || !clientSecret.trim()) {
      setError('Vui lòng nhập Client ID và Client Secret.');
      return;
    }
    setError('');
    sessionStorage.setItem(STORAGE_KEYS.storeName, storeName.trim());
    sessionStorage.setItem(STORAGE_KEYS.clientId, clientId.trim());
    sessionStorage.setItem(STORAGE_KEYS.clientSecret, clientSecret.trim());
    sessionStorage.setItem(STORAGE_KEYS.shop, shop.trim());

    // Redirect thay vì fetch → không bị CORS (điều hướng trang, không phải XHR).
    // Fetch từ browser (localhost hay domain bất kì) sang Shopify đều bị CORS vì Shopify không gửi Access-Control-Allow-Origin.
    const redirectUri = `${window.location.origin}/token`;
    const authUrl = `https://${shop.trim()}/admin/oauth/authorize?client_id=${encodeURIComponent(clientId.trim())}&redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = authUrl;
  };

  const handleOpenNewTab = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('form', '1');
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const showForm = searchParams.get('form') === '1';
  const activeStep = !shop ? 0 : !code && !accessTokenResult ? 1 : 2;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        py: 4,
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 560, mx: 'auto' }}>
        <Typography
          variant="h4"
          sx={{
            color: '#eee',
            fontWeight: 700,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <VpnKeyIcon fontSize="large" /> Lấy Access Token
        </Typography>
        <Typography variant="body2" sx={{ color: '#aaa', mb: 3 }}>
          Làm lần lượt các bước bên dưới. Kết quả cuối là access token từ Shopify.
        </Typography>

        <Stepper
          activeStep={activeStep}
          sx={{
            mb: 3,
            '& .MuiStepLabel-root': { flexDirection: 'column', alignItems: 'center' },
            '& .MuiStepLabel-iconContainer': { marginRight: 0, marginBottom: 4 },
            '& .MuiStepLabel-label': { color: '#fff', textAlign: 'center' },
            '& .MuiStepIcon-root': { color: 'rgba(255,255,255,0.5)' },
            '& .MuiStepIcon-root.Mui-active': { color: '#E45F2B' },
            '& .MuiStepIcon-root.Mui-completed': { color: '#81c784' },
            '& .MuiStepConnector-line': { borderColor: 'rgba(255,255,255,0.3)' },
          }}
        >
          <Step>
            <StepLabel>Lấy shop từ URL</StepLabel>
          </Step>
          <Step>
            <StepLabel>Client ID / Secret & Authorize</StepLabel>
          </Step>
          <Step>
            <StepLabel>Kết quả access token</StepLabel>
          </Step>
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Bước 1: Thông báo đã lấy shop */}
        {shop && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'rgba(255,255,255,0.08)', color: '#e0e0e0' }}>
            <Typography variant="subtitle2" color="primary.light">
              Bước 1 đã xong
            </Typography>
            <Typography variant="body2">
              Shop: <strong>{shop}</strong>

            </Typography>
          </Paper>
        )}

        {/* Bước 2: Chưa mở tab mới → chỉ nút "Mở tab mới". Đã mở (form=1) → hiện form */}
        {shop && !accessTokenResult && (
          <Paper sx={{ p: 3, mb: 2, bgcolor: 'rgba(255,255,255,0.08)' }}>
            <Typography variant="subtitle1" sx={{ color: '#e0e0e0', mb: 2 }}>
              Bước 2: {showForm ? 'Nhập form và chuyển sang Shopify authorize.' : 'Bấm Mở tab mới (trang sẽ mở trong tab mới và hiện form ở đó).'}
            </Typography>
            {!showForm ? (
              <Button
                variant="outlined"
                onClick={handleOpenNewTab}
                sx={{ borderColor: '#81c784', color: '#81c784', '&:hover': { borderColor: '#66bb6a', bgcolor: 'rgba(129,199,132,0.1)' }, }}
              >
                Connect với Fullfillment
              </Button>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Tên store"
                  placeholder="domain.com"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{ sx: { bgcolor: 'rgba(255,255,255,0.9)' } }}
                />
                <TextField
                  fullWidth
                  label="Client ID"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                  InputProps={{ sx: { bgcolor: 'rgba(255,255,255,0.9)' } }}
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Client Secret"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                  InputProps={{ sx: { bgcolor: 'rgba(255,255,255,0.9)' } }}
                />

                <Alert severity="info" sx={{ mb: 2 }}>
                  Đổi code lấy access token gọi qua proxy (tránh CORS). Dev: chạy <code>npm run server</code> rồi <code>npm start</code>. Production: dùng <code>npm run serve</code> (server-production.js) hoặc set <code>REACT_APP_TOKEN_API_URL</code> trỏ tới backend có route /api/shopify/access_token.
                </Alert>
                <Button
                  variant="contained"
                  onClick={handleAuthorize}
                  disabled={loading}
                  sx={{ bgcolor: '#E45F2B', '&:hover': { bgcolor: '#d14e1f' } }}
                >
                  Chuyển sang Shopify Authorize
                </Button>
              </>
            )}
          </Paper>
        )}

        {/* Đang gọi POST access_token */}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ color: '#aaa' }}>Đang đổi code lấy access token...</Typography>
          </Box>
        )}

        {/* Đang gọi addshopify */}
        {addShopifyLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ color: '#aaa' }}>Đang thêm store vào hệ thống fulfilment...</Typography>
          </Box>
        )}

        {/* Kết quả addshopify */}
        {addShopifyMessage && (
          <Alert severity={addShopifyMessage.type} sx={{ mb: 2 }}>
            {addShopifyMessage.text}
          </Alert>
        )}

        {/* Bước 3: Hiển thị kết quả */}
        {accessTokenResult && (
          <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.08)' }}>
            <Typography variant="subtitle1" sx={{ color: '#e0e0e0', mb: 2 }}>
              Kết quả trả về (access token)
            </Typography>
            <Paper
              component="pre"
              sx={{
                p: 2,
                overflow: 'auto',
                maxHeight: 400,
                bgcolor: '#1a1a2e',
                color: accessTokenResult.error ? '#ff8a80' : '#81c784',
                textAlign: 'left',
              }}
            >
              {JSON.stringify(accessTokenResult.error ? accessTokenResult.body || accessTokenResult : accessTokenResult.data, null, 2)}
            </Paper>
            {accessTokenResult.data?.access_token && (
              <Typography sx={{ mt: 2, color: '#81c784', wordBreak: 'break-all' }}>
                Access token: {accessTokenResult.data.access_token}
              </Typography>
            )}
          </Paper>
        )}

        {!shop && (
          <Alert severity="info">
            Truy cập trang này kèm params, ví dụ: <code>?embedded=1&shop=e0gg0c-s0.myshopify.com&...</code> để lấy shop.
          </Alert>
        )}
      </Box>
    </Box>
  );
}

export default Token;
